import { useEffect, useState } from "react";
import api from "@/lib/api";
import { useI18n } from "@/context/I18nContext";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { ShieldCheck, GraduationCap, AlertTriangle, RefreshCcw, FileSpreadsheet } from "lucide-react";

const T = {
  tr: {
    title: "Veri Doğrulama",
    desc: "Aylık muhasebe doğrulama durumu",
    learningScore: "Öğrenme skoru",
    verified: "Doğrulanmış",
    learning: "Öğrenme",
    incomplete: "Eksik",
    viewMonths: "Ayları görüntüle",
    monthsTitle: "Muhasebe Ayları",
    bank: "Banka",
    revenue: "Ciro",
    saldenliste: "Saldenliste",
    status: "Durum",
    month: "Ay",
    confidence: "Güven",
    notes: "Notlar",
    close: "Kapat",
    refresh: "Yenile",
    saldToggleTitle: "Saldenliste durumu",
    saldOn: "İçe aktarıldı",
    saldOff: "İçe aktarılmadı",
    saldNotes: "Not (opsiyonel)",
    save: "Kaydet",
    noData: "Henüz veri yok",
  },
  de: {
    title: "Datenverifizierung",
    desc: "Verifizierungsstatus je Buchungsmonat",
    learningScore: "Lernwert",
    verified: "Verifiziert",
    learning: "Lernend",
    incomplete: "Unvollständig",
    viewMonths: "Monate ansehen",
    monthsTitle: "Buchungsmonate",
    bank: "Bank",
    revenue: "Umsatz",
    saldenliste: "Saldenliste",
    status: "Status",
    month: "Monat",
    confidence: "Konfidenz",
    notes: "Notiz",
    close: "Schließen",
    refresh: "Aktualisieren",
    saldToggleTitle: "Saldenliste Status",
    saldOn: "Importiert",
    saldOff: "Nicht importiert",
    saldNotes: "Notiz (optional)",
    save: "Speichern",
    noData: "Noch keine Daten",
  },
};

const STATUS_CLASS = {
  verified:   "bg-emerald-100 text-emerald-800 border-emerald-200",
  learning:   "bg-amber-100 text-amber-800 border-amber-200",
  incomplete: "bg-rose-100 text-rose-800 border-rose-200",
};

export default function DataVerificationCard() {
  const { lang } = useI18n();
  const t = T[lang] || T.tr;
  const [summary, setSummary] = useState(null);
  const [months, setMonths] = useState([]);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [editing, setEditing] = useState(null);  // {month_key, imported, notes}

  const load = async () => {
    try {
      const [s, m] = await Promise.all([
        api.get("/accounting-months/summary"),
        api.get("/accounting-months"),
      ]);
      setSummary(s.data);
      setMonths(m.data || []);
    } catch (_) {}
  };
  useEffect(() => { load(); }, []);

  const doRefresh = async () => {
    setBusy(true);
    try {
      await api.post("/accounting-months/refresh");
      await load();
      toast.success(t.refresh);
    } finally { setBusy(false); }
  };

  const saveSald = async () => {
    if (!editing) return;
    setBusy(true);
    try {
      await api.post(`/accounting-months/${editing.month_key}/saldenliste`, {
        imported: editing.imported, notes: editing.notes || null,
      });
      await load();
      setEditing(null);
      toast.success("✓");
    } finally { setBusy(false); }
  };

  if (!summary) return null;
  if (summary.total === 0) {
    return null;  // don't render an empty widget on fresh tenants
  }

  return (
    <>
      <Card data-testid="data-verification-card">
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center justify-between text-base">
            <span className="flex items-center gap-2">
              <ShieldCheck className="w-4 h-4 text-primary" />{t.title}
            </span>
            <div className="flex items-center gap-2">
              <Button size="sm" variant="ghost" onClick={doRefresh} disabled={busy}
                      data-testid="data-verification-refresh-btn" className="h-7 px-2">
                <RefreshCcw className={`w-3.5 h-3.5 ${busy ? "animate-spin" : ""}`} />
              </Button>
              <Button size="sm" variant="outline" onClick={() => setOpen(true)}
                      data-testid="data-verification-open-btn" className="h-7">
                {t.viewMonths}
              </Button>
            </div>
          </CardTitle>
          <p className="text-xs text-muted-foreground">{t.desc}</p>
        </CardHeader>
        <CardContent className="grid grid-cols-4 gap-3">
          <ScoreStat label={t.learningScore}
                      value={`${summary.learning_score}`}
                      big
                      accent={summary.learning_score >= 70 ? "emerald" : summary.learning_score >= 40 ? "amber" : "rose"}
                      testid="data-verification-score" />
          <MiniStat label={t.verified} value={summary.verified} accent="emerald"
                    Icon={ShieldCheck} testid="data-verification-verified" />
          <MiniStat label={t.learning} value={summary.learning} accent="amber"
                    Icon={GraduationCap} testid="data-verification-learning" />
          <MiniStat label={t.incomplete} value={summary.incomplete}
                    accent={summary.incomplete ? "rose" : undefined}
                    Icon={AlertTriangle} testid="data-verification-incomplete" />
        </CardContent>
      </Card>

      {/* Months dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl" data-testid="data-verification-months-dialog">
          <DialogHeader>
            <DialogTitle>{t.monthsTitle}</DialogTitle>
          </DialogHeader>
          <div className="overflow-x-auto -mx-6">
            <table className="w-full text-sm">
              <thead className="border-b bg-muted/40 text-xs uppercase text-muted-foreground">
                <tr>
                  <th className="text-left px-6 py-2">{t.month}</th>
                  <th className="text-center px-3 py-2">{t.status}</th>
                  <th className="text-center px-3 py-2">{t.confidence}</th>
                  <th className="text-center px-3 py-2">{t.bank}</th>
                  <th className="text-center px-3 py-2">{t.revenue}</th>
                  <th className="text-center px-3 py-2">{t.saldenliste}</th>
                  <th className="text-center px-6 py-2"></th>
                </tr>
              </thead>
              <tbody>
                {months.map((m) => (
                  <tr key={m.month_key} className="border-b last:border-0"
                      data-testid={`month-row-${m.month_key}`}>
                    <td className="px-6 py-2 font-mono">{m.month_key}</td>
                    <td className="px-3 py-2 text-center">
                      <Badge variant="outline" className={`${STATUS_CLASS[m.verification_status]} text-xs`}>
                        {t[m.verification_status]}
                      </Badge>
                    </td>
                    <td className="px-3 py-2 text-center text-xs">
                      {(m.confidence_score * 100).toFixed(0)}%
                    </td>
                    <td className="px-3 py-2 text-center">{m.bank_imported ? "✓" : "—"}</td>
                    <td className="px-3 py-2 text-center">{m.revenue_imported ? "✓" : "—"}</td>
                    <td className="px-3 py-2 text-center">{m.saldenliste_imported ? "✓" : "—"}</td>
                    <td className="px-6 py-2 text-right">
                      <Button size="sm" variant="ghost" className="h-7 px-2 text-xs"
                              onClick={() => setEditing({
                                month_key: m.month_key,
                                imported: !!m.saldenliste_imported,
                                notes: m.notes || "",
                              })}
                              data-testid={`month-sald-toggle-${m.month_key}`}>
                        <FileSpreadsheet className="w-3.5 h-3.5" />
                      </Button>
                    </td>
                  </tr>
                ))}
                {months.length === 0 && (
                  <tr><td colSpan="7" className="px-6 py-6 text-center text-xs text-muted-foreground">{t.noData}</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </DialogContent>
      </Dialog>

      {/* Saldenliste toggle dialog */}
      <Dialog open={!!editing} onOpenChange={(v) => !v && setEditing(null)}>
        <DialogContent className="max-w-md" data-testid="data-verification-sald-dialog">
          <DialogHeader>
            <DialogTitle>{t.saldToggleTitle} — <span className="font-mono">{editing?.month_key}</span></DialogTitle>
          </DialogHeader>
          {editing && (
            <div className="space-y-4">
              <div className="flex items-center justify-between border rounded-lg p-3">
                <div className="text-sm">{editing.imported ? t.saldOn : t.saldOff}</div>
                <Switch checked={editing.imported}
                        onCheckedChange={(v) => setEditing({ ...editing, imported: v })}
                        data-testid="sald-switch" />
              </div>
              <div>
                <label className="text-xs uppercase tracking-wide text-muted-foreground">{t.saldNotes}</label>
                <Textarea rows={3} value={editing.notes}
                          onChange={(e) => setEditing({ ...editing, notes: e.target.value })}
                          data-testid="sald-notes" />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditing(null)}>{t.close}</Button>
            <Button onClick={saveSald} disabled={busy} data-testid="sald-save-btn">{t.save}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

function ScoreStat({ label, value, accent, big, testid }) {
  const color = accent === "emerald" ? "text-emerald-700"
              : accent === "amber"   ? "text-amber-700"
              : accent === "rose"    ? "text-rose-600" : "text-foreground";
  return (
    <div className="border rounded-xl p-3 bg-card">
      <div className="text-[10px] uppercase tracking-wide text-muted-foreground">{label}</div>
      <div className={`${big ? "text-2xl" : "text-xl"} font-semibold ${color}`} data-testid={testid}>{value}</div>
    </div>
  );
}

function MiniStat({ label, value, accent, Icon, testid }) {
  const color = accent === "emerald" ? "text-emerald-700"
              : accent === "amber"   ? "text-amber-700"
              : accent === "rose"    ? "text-rose-600" : "text-muted-foreground";
  return (
    <div className="border rounded-xl p-3 bg-card">
      <div className="flex items-center justify-between">
        <div className="text-[10px] uppercase tracking-wide text-muted-foreground">{label}</div>
        {Icon && <Icon className={`w-3.5 h-3.5 ${color}`} />}
      </div>
      <div className={`text-xl font-semibold ${color}`} data-testid={testid}>{value}</div>
    </div>
  );
}
